import React, { useRef } from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, useInView } from 'framer-motion';

/**
 * TwoColumnLayout component - Split content with slide-in animations
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.leftContent - Content for left column (slides from left)
 * @param {React.ReactNode} props.rightContent - Content for right column (slides from right)
 * @param {string} props.marginTop - Top margin
 * @param {string} props.marginBottom - Bottom margin
 * @returns {React.Component} TwoColumnLayout component
 */
const TwoColumnLayout = ({
  leftContent,
  rightContent,
  marginTop = '0',
  marginBottom = '0',
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { threshold: 0.2 });

  const leftVariants = {
    hidden: {
      x: -100,
      opacity: 0
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 1.0,
        ease: [0.43, 0.13, 0.23, 0.96]
      }
    }
  };

  const rightVariants = {
    hidden: {
      x: 100,
      opacity: 0
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 1.0,
        ease: [0.43, 0.13, 0.23, 0.96],
        delay: 0.3 // Slight delay for stagger effect
      }
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        marginTop: marginTop,
        marginBottom: marginBottom,
        ...props.sx
      }}
    >
      <Grid container spacing={isMobile ? 2 : 4} sx={{ alignItems: 'stretch' }}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Box
            component={motion.div}
            initial='hidden'
            animate={isInView ? 'visible' : 'hidden'}
            variants={leftVariants}
            sx={{ height: '100%' }}
          >
            {leftContent}
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Box
            component={motion.div}
            initial='hidden'
            animate={isInView ? 'visible' : 'hidden'}
            variants={rightVariants}
            sx={{ height: '100%' }}
          >
            {rightContent}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TwoColumnLayout;
